import { useCallback, useState } from "react";

/**
 * useLocalStorageSet
 *
 * A Set<string> whose membership persists to localStorage, with a single
 * `toggle(id)` to add/remove an item. Built for exactly this shape of
 * problem — "is this id in a persisted collection, and can the user flip
 * it" — which shows up twice on this page alone (bookmarked video ids,
 * watched video ids) and is a common enough UI pattern (favorites,
 * read/unread, dismissed items, etc.) to be worth its own file rather
 * than copy-pasted per feature.
 *
 * SSR-safe: falls back to an empty Set when `window` isn't available
 * (build-time rendering, tests) instead of throwing.
 *
 * @param {string} storageKey - localStorage key this set is stored under.
 *   Callers should namespace this per-feature, e.g. "yog:bookmarks".
 * @returns {[Set<string>, (id: string) => void]} the current set, and a
 *   toggle function that adds the id if absent or removes it if present.
 *
 * @example
 *   const [bookmarked, toggleBookmark] = useLocalStorageSet("yog:bookmarks");
 *   const isBookmarked = bookmarked.has(video.videoId);
 *   <button onClick={() => toggleBookmark(video.videoId)}>...</button>
 */
export default function useLocalStorageSet(storageKey) {
  const [set, setSet] = useState(() => {
    if (typeof window === "undefined") {
      return new Set();
    }

    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (err) {
      // Corrupt/blocked storage shouldn't crash the page — just start empty.
      // eslint-disable-next-line no-console
      console.warn(
        `[useLocalStorageSet] Couldn't read "${storageKey}" from localStorage:`,
        err
      );
      return new Set();
    }
  });

  const toggle = useCallback(
    (id) => {
      setSet((prev) => {
        const next = new Set(prev);

        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }

        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(
              storageKey,
              JSON.stringify([...next])
            );
          } catch (err) {
            // Storage full/blocked (private browsing, etc.) — the in-memory
            // state still updates, it just won't survive a reload.
            // eslint-disable-next-line no-console
            console.warn(
              `[useLocalStorageSet] Couldn't persist "${storageKey}":`,
              err
            );
          }
        }

        return next;
      });
    },
    [storageKey]
  );

  return [set, toggle];
}
